<template>
  <div class="turnstile-container">
    <!-- 加载状态占位符 -->
    <div v-if="!isLoaded" class="turnstile-placeholder">
      <div v-if="!loadFailed" class="loading-spinner"></div>
      <p class="loading-text">{{ $t("cpts.turnstile.loading") }}</p>
      <button 
        v-if="loadFailed" 
        @click="retryLoad" 
        class="retry-button"
      >
        {{ $t("cpts.turnstile.actions.reload") }}
      </button>
    </div>
    
    <!-- Turnstile 容器 -->
    <div 
      ref="turnstileBox" 
      id="turnstile-box" 
      data-theme="light"
      :class="{ 'hidden': !isLoaded }"
    ></div>
  </div>
</template>

<script>
export default {
  name: "TurnstileWidget",
  props: {
    sitekey: {
      type: String,
      required: true,
    },
  },
  emits: ["verify", "expire", "fail"],
  data() {
    return {
      isLoaded: false,
      loadFailed: false,
      retryCount: 0,
      maxRetries: 3,
      loadTimeout: null,
    };
  },
  mounted() {
    this.initTurnstile();
  },
  beforeDestroy() {
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
    }
  },
  methods: {
    initTurnstile() {
      this.isLoaded = false;
      this.loadFailed = false;
      
      // 设置加载超时
      this.loadTimeout = setTimeout(() => {
        if (!this.isLoaded) {
          this.handleLoadFailure();
        }
      }, 10000); // 10秒超时

      if (!window.turnstile) {
        const script = document.createElement("script");
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback";
        script.async = true;
        script.defer = true;
        
        script.onerror = () => {
          this.handleLoadFailure();
        };
        
        document.head.appendChild(script);
      }

      this.renderTurnstile();
    },
    
    renderTurnstile() {
      window.onloadTurnstileCallback = () => {
        try {
          if (window.turnstile) {
            window.turnstile.render("#turnstile-box", {
              sitekey: this.sitekey,
              callback: (response) => {
                this.isLoaded = true;
                this.$emit("verify", response);
              },
              "expired-callback": () => this.$emit("expire"),
              "error-callback": () => {
                this.loadFailed = true;
                this.$emit("fail");
              },
            });
            
            // 清除超时
            if (this.loadTimeout) {
              clearTimeout(this.loadTimeout);
            }
            
            this.isLoaded = true;
          } else {
            this.handleLoadFailure();
          }
        } catch (error) {
          console.error('Turnstile render error:', error);
          this.handleLoadFailure();
        }
      };
      
      if (window.turnstile) {
        window.onloadTurnstileCallback();
      }
    },
    
    handleLoadFailure() {
      this.loadFailed = true;
      this.isLoaded = false;
      
      if (this.loadTimeout) {
        clearTimeout(this.loadTimeout);
      }
      
      console.error('Turnstile failed to load');
    },
    
    retryLoad() {
      if (this.retryCount >= this.maxRetries) {
        this.$message.error(this.$t("cpts.turnstile.messages.retry_limit"));
        return;
      }
      
      this.retryCount++;
      this.initTurnstile();
    },
  },
};
</script>

<style scoped>
.turnstile-container {
  position: relative;
  min-height: 65px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.turnstile-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  min-height: 65px;
  width: 100%;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 8px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  margin: 0;
  font-size: 14px;
  color: #666;
  text-align: center;
}

.retry-button {
  margin-top: 8px;
  padding: 6px 12px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: background-color 0.2s;
}

.retry-button:hover {
  background: #0056b3;
}

.hidden {
  display: none;
}
</style>